import React, { Component } from 'react';
import { array, bool, func, number, shape, string } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { withViewport } from '../../util/contextHelpers';
import { parse, stringify } from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  Button,
  ExternalLink,
  LimitedAccessBanner,
  Logo,
  Modal,
  ModalMissingInformation,
  NamedLink,
  TopbarDesktop,
  TopbarMobileMenu,
} from '../../components';
import { TopbarSearchForm } from '../../forms';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import { ADMIN_ROLE, BUYER_ROLE, COACHEE_ROLE, COACH_ROLE } from '../../marketplace-custom-config';
import { getReportData } from '../../util/api';
import { utils, writeFile } from 'xlsx';
import MyCoachingPopup from '../MyCoachingPopup/MyCoachingPopup';

import css from './Topbar.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const coachHomepageUrl = '/become-a-coach';
const SHEET_NAME = 'Coaches';
const REPORT_HEADER = [
  'Email',
  'First name',
  'Last name',
  'User type',
  'Opt in',
  'Account status',
  'Claim date (en)',
];
const STARTING_POINT = 'A1';
const REPORT_FILE_NAME = 'weekly-report.xlsx';
const COLUMN_WIDTH = [
  { wch: 30 },
  { wch: 15 },
  { wch: 15 },
  { wch: 10 },
  { wch: 10 },
  { wch: 20 },
  { wch: 15 },
];

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleDownloadReport = this.handleDownloadReport.bind(this);
    this.handleSendMedooLoginLink = this.handleSendMedooLoginLink.bind(this);
    this.state = {
      isOpenMyCoachingPopup: false,
    };
  }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    redirectToURLWithModalState(this.props, 'mobilesearch');
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const keywords = values.keywords;
    const { history } = this.props;
    const searchParams = {
      ...currentSearchParams,
      keywords,
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  }

  handleLogout() {
    const { onLogout, history } = this.props;
    onLogout().then(() => {
      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
        window.location.href = '/';
      console.log('logged out'); // eslint-disable-line
    });
  }

  async handleDownloadReport() {
    const response = await getReportData();
    const reportData = response.data;

    const worksheet = utils.json_to_sheet(reportData);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, SHEET_NAME);
    utils.sheet_add_aoa(worksheet, [REPORT_HEADER], { origin: STARTING_POINT });

    // Set column widths
    worksheet['!cols'] = [...COLUMN_WIDTH];
    writeFile(workbook, REPORT_FILE_NAME, { compression: true });
  }

  handleSendMedooLoginLink() {
    const { onSendMedooLoginLink } = this.props;
    this.setState({
      isOpenMyCoachingPopup: true,
    });
    onSendMedooLoginLink();
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      authScopes,
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserListing,
      currentUserListingFetched,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      viewport,
      intl,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      hideSearchbar,
      history,
      handleMoveToCoachingCultureSection,
      accessibleEngagementList,
      showMissingInformationModal,
      sendLoginLinkInProgress,
      sendLoginLinkError,
      onSendMedooLoginLink,
    } = this.props;

    const isCoachHomePage = history.location.pathname === coachHomepageUrl;

    const { role } = currentUser?.attributes?.profile?.publicData || {};
    const { hasMedooAccount, sentMedooLoginLink: hasSentMedooLoginLink } =
      currentUser?.attributes?.profile.metadata ?? {};

    const isCoach = role === COACH_ROLE;
    const isCoachee = role === COACHEE_ROLE;
    const isAdmin = role === ADMIN_ROLE;
    const isBuyer = role === BUYER_ROLE;

    const { mobilemenu, mobilesearch, keywords } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

    const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUserListing={currentUserListing}
        currentUserListingFetched={currentUserListingFetched}
        currentUser={currentUser}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={currentPage}
        hideSearchbar={hideSearchbar}
        isCoachHomePage={isCoachHomePage}
        handleMoveToCoachingCultureSection={handleMoveToCoachingCultureSection}
        onClose={this.handleMobileMenuClose}
        accessibleEngagementList={accessibleEngagementList}
        onDownloadReport={this.handleDownloadReport}
        handleSendMedooLoginLink={this.handleSendMedooLoginLink}
        isCoach={isCoach}
        isCoachee={isCoachee}
        isAdmin={isAdmin}
        isBuyer={isBuyer}
        hasMedooAccount={hasMedooAccount}
        hasSentMedooLoginLink={hasSentMedooLoginLink}
        config={config}
      />
    );

    const initialSearchFormValues = {
      keywords
    };

    const classes = classNames(rootClassName || css.root, className);

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={currentPage}
        />
        <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
          <Button
            rootClassName={css.menu}
            onClick={this.handleMobileMenuOpen}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <MenuIcon className={css.menuIcon} />
            {notificationDot}
          </Button>
          <ExternalLink
            href={isCoach ? '/become-a-coach' : '/'}
            target="_self"
            className={css.home}
            title={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          >
            <Logo format="mobile" />
          </ExternalLink>
          {hideSearchbar || isCoachee ? (
            <div className={css.emptyDiv} />
          ) : (
            <Button
              rootClassName={css.searchMenu}
              onClick={this.handleMobileSearchOpen}
              title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
            >
              <SearchIcon className={css.searchMenuIcon} />
            </Button>
          )}
        </div>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUserListing={currentUserListing}
            currentUserListingFetched={currentUserListingFetched}
            currentUser={currentUser}
            currentPage={currentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            hideSearchbar={hideSearchbar}
            isCoachHomePage={isCoachHomePage}
            handleMoveToCoachingCultureSection={handleMoveToCoachingCultureSection}
            accessibleEngagementList={accessibleEngagementList}
            onDownloadReport={this.handleDownloadReport}
            isCoach={isCoach}
            isCoachee={isCoachee}
            isAdmin={isAdmin}
            isBuyer={isBuyer}
            hasMedooAccount={hasMedooAccount}
            hasSentMedooLoginLink={hasSentMedooLoginLink}
            handleSendMedooLoginLink={this.handleSendMedooLoginLink}
            config={config}
          />
        </div>
        <Modal
          id="TopbarMobileMenu"
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>
        <Modal
          id="TopbarMobileSearch"
          containerClassName={css.modalContainer}
          isOpen={isMobileSearchOpen}
          onClose={this.handleMobileSearchClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <TopbarSearchForm
              onSubmit={this.handleSubmit}
              initialValues={initialSearchFormValues}
              isMobile
              isShowSearchBar={!isCoach}
            />
          </div>
        </Modal>
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
          showMissingInformationModal={showMissingInformationModal}
        />

        <GenericError show={showGenericError} />

        <MyCoachingPopup
          isOpen={this.state.isOpenMyCoachingPopup}
          onClose={() =>
            this.setState({
              isOpenMyCoachingPopup: false,
            })
          }
          onManageDisableScrolling={onManageDisableScrolling}
          sendLoginLinkInProgress={sendLoginLinkInProgress}
          sendLoginLinkError={sendLoginLinkError}
          onSendMedooLoginLink={onSendMedooLoginLink}
        />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
  handleMoveToCoachingCultureSection: () => {},
  accessibleEngagementList: [],
  sendLoginLinkInProgress: false,
  sendLoginLinkError: null,
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,
  handleMoveToCoachingCultureSection: func,
  accessibleEngagementList: array,
  sendLoginLinkInProgress: bool,
  sendLoginLinkError: propTypes.error,
  onSendMedooLoginLink: func.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const Topbar = compose(
  withViewport,
  injectIntl
)(TopbarComponent);

Topbar.displayName = 'Topbar';

export default Topbar;
